import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  CaretDown as CaretDownIcon,
  CheckCircle as CheckCircleIcon,
  WarningOctagon as WarningOctagonIcon,
  BellSimpleRinging as BellSimpleRingingIcon,
  ArrowCounterClockwise as ArrowCounterClockwiseIcon,
  Check as CheckIcon,
  ArrowRight as ArrowRightIcon,
  ChartLineUp as ChartLineUpIcon,
  Sparkle as SparkleIcon,
  ChatsCircle as ChatsCircleIcon,
} from '@phosphor-icons/react';
import type { AllocationRow, AllocStats } from '../data';
import { SNAPSHOTS, findCustomer, findProduct, buildDigest } from '../data';

function Stat({
  label,
  value,
  suffix,
  tone,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  tone: 'danger' | 'warn' | 'good' | 'mute';
}) {
  const colors = {
    danger: 'text-red-800',
    warn: 'text-amber-800',
    good: 'text-emerald-800',
    mute: 'text-stone-700',
  };
  return (
    <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
      <span
        className={cn(
          'text-sm font-bold tabular-nums tracking-tight',
          colors[tone],
        )}
      >
        {value}
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {suffix ? ` ${suffix}` : ''}
      </span>
    </span>
  );
}

function SectionHead({
  icon: Icon,
  tint,
  children,
}: {
  icon: React.ComponentType<any>;
  tint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5 whitespace-nowrap">
      <Icon size={12} style={{ color: tint }} />
      <span className="text-[10px] font-bold uppercase tracking-wider text-stone-700">
        {children}
      </span>
    </div>
  );
}

export function ReviewCard({
  stats,
  filtered,
  compare,
  dense,
  onJumpTo,
}: {
  stats: AllocStats;
  filtered: AllocationRow[];
  compare: { from: string; to: string };
  dense: boolean;
  onJumpTo?: (rid: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const [acked, setAcked] = useState(false);

  const movers = useMemo(() => {
    return filtered
      .map((r) => ({ r, delta: r.qty[compare.to] - r.qty[compare.from] }))
      .filter((x) => x.delta !== 0)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 4);
  }, [filtered, compare]);

  const overRows = useMemo(
    () => filtered.filter((r) => r.remainder < 0).slice(0, 4),
    [filtered],
  );

  const fromSnap = SNAPSHOTS.find((s) => s.id === compare.from)!;
  const toSnap = SNAPSHOTS.find((s) => s.id === compare.to)!;
  const hasIssues = stats.overConsumed > 0;

  const stateConfig = acked
    ? {
        fg: 'text-emerald-800',
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        Icon: CheckCircleIcon,
      }
    : hasIssues
      ? {
          fg: 'text-red-800',
          bg: 'bg-red-50',
          border: 'border-red-200',
          Icon: WarningOctagonIcon,
        }
      : stats.changedRows > 0
        ? {
            fg: 'text-amber-800',
            bg: 'bg-amber-50',
            border: 'border-amber-300',
            Icon: BellSimpleRingingIcon,
          }
        : {
            fg: 'text-stone-700',
            bg: 'bg-stone-50',
            border: 'border-border',
            Icon: CheckCircleIcon,
          };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-white shadow-xs">
      {/* COLLAPSED HEADER */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        className={cn(
          'flex w-full cursor-pointer items-center gap-3 text-left',
          dense ? 'px-3.5 py-2.5' : 'px-4 py-3.5',
        )}
      >
        <span
          className={cn(
            'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border text-base',
            stateConfig.bg,
            stateConfig.fg,
            stateConfig.border,
          )}
        >
          <stateConfig.Icon size={16} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="whitespace-nowrap text-sm font-semibold tracking-tight text-stone-900">
              {acked ? 'Snapshot acknowledged' : 'Snapshot review'}
            </span>
            <span className="whitespace-nowrap text-[11px] text-muted-foreground">
              {fromSnap.shortLabel.replace(' W', '')} →{' '}
              {toSnap.shortLabel.replace(' W', '')}
              {toSnap.kind === 'weekly' && (
                <span className="ml-1 rounded bg-indigo-50 px-1.5 text-[9px] font-bold text-indigo-700">
                  ANCHOR
                </span>
              )}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3.5 gap-y-1">
            <Stat
              label="Changed"
              value={stats.changedRows}
              tone={stats.changedRows > 0 ? 'warn' : 'mute'}
            />
            <Stat
              label="Over-consumed"
              value={stats.overConsumed}
              tone={stats.overConsumed > 0 ? 'danger' : 'good'}
            />
            <Stat
              label="Allocated"
              value={stats.totalAlloc.toLocaleString()}
              suffix="units"
              tone="mute"
            />
            <Stat label="Customers" value={stats.totalCustomers} tone="mute" />
            <Stat label="Source WHs" value={stats.whCount} tone="mute" />
          </div>
        </div>

        <div
          className="flex flex-shrink-0 items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {!acked ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setAcked(true);
              }}
              className="inline-flex items-center gap-1.5 rounded-md bg-stone-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-xs hover:bg-stone-800"
            >
              <CheckIcon size={12} />
              Acknowledge
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setAcked(false);
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs font-medium text-stone-800"
            >
              <ArrowCounterClockwiseIcon size={12} />
              Re-open
            </button>
          )}
          <span
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-transform duration-200"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)' }}
          >
            <CaretDownIcon size={14} />
          </span>
        </div>
      </div>

      {/* EXPANDED BODY */}
      {open && (
        <div
          className={cn(
            'grid grid-cols-3 gap-3.5 border-t border-border bg-stone-50',
            dense ? 'px-3.5 py-3 pb-3.5' : 'px-4 py-3.5 pb-4',
          )}
        >
          {/* AI digest */}
          <div className="rounded-lg border border-border bg-white p-2.5 px-3">
            <div className="mb-1.5 flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded bg-gradient-to-r from-violet-100 to-sky-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-violet-700">
                <SparkleIcon size={9} /> AI digest
              </span>
              <span className="text-[10px] text-muted-foreground">
                run on {toSnap.label}
              </span>
            </div>
            <p className="m-0 text-xs leading-relaxed text-stone-800">
              {buildDigest(filtered, compare, stats)}
            </p>
            <button className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] font-medium text-violet-700 hover:bg-violet-100">
              <ChatsCircleIcon size={12} />
              Chat about this
            </button>
          </div>

          {/* Top movers */}
          <div>
            <SectionHead icon={ChartLineUpIcon} tint="#4338ca">
              Largest moves
            </SectionHead>
            <div className="flex flex-col gap-1">
              {movers.length === 0 && (
                <div className="py-1.5 text-[11px] text-muted-foreground">
                  No changes vs comparison.
                </div>
              )}
              {movers.map(({ r, delta }) => {
                const cust = findCustomer(r.customer);
                const prod = findProduct(r.product);
                const isUp = delta > 0;
                return (
                  <div
                    key={r.rid}
                    onClick={() => onJumpTo?.(r.rid)}
                    className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-white p-1.5 px-2 hover:bg-stone-50"
                  >
                    <span
                      className={cn(
                        'inline-flex min-w-[36px] items-center justify-center rounded px-1.5 py-px text-[11px] font-bold tabular-nums',
                        isUp
                          ? 'border border-emerald-200 bg-emerald-50 text-emerald-800'
                          : 'border border-red-200 bg-red-50 text-red-800',
                      )}
                    >
                      {isUp ? '+' : ''}
                      {delta}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[11px] font-medium text-stone-900">
                        {cust.name}
                      </div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {prod.id} · {prod.name}
                      </div>
                    </div>
                    <ArrowRightIcon
                      size={10}
                      className="text-muted-foreground"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Over-consumed */}
          <div>
            <SectionHead
              icon={
                stats.overConsumed > 0 ? WarningOctagonIcon : CheckCircleIcon
              }
              tint={stats.overConsumed > 0 ? '#dc2626' : '#16a34a'}
            >
              Over-consumption{' '}
              {stats.overConsumed > 0 && (
                <span className="font-bold text-red-800">
                  · {stats.overConsumed}
                </span>
              )}
            </SectionHead>
            <div className="flex flex-col gap-1">
              {overRows.length === 0 && (
                <div className="flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50/50 p-2.5 text-[11px] text-emerald-800">
                  <CheckCircleIcon
                    size={13}
                    className="flex-shrink-0 text-emerald-600"
                  />
                  All confirmations within allocation.
                </div>
              )}
              {overRows.map((r) => {
                const cust = findCustomer(r.customer);
                const prod = findProduct(r.product);
                return (
                  <div
                    key={r.rid}
                    onClick={() => onJumpTo?.(r.rid)}
                    className="flex cursor-pointer items-center gap-1.5 rounded-md border border-red-200 bg-red-50/50 p-1.5 px-2"
                  >
                    <span className="inline-flex min-w-[36px] items-center justify-center rounded bg-red-100 px-1.5 py-px text-[11px] font-bold tabular-nums text-red-800">
                      {r.remainder}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[11px] font-medium text-stone-900">
                        {cust.name}
                      </div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {prod.id} · consumed {r.consumed}/{r.qty['S-MAY13']}{' '}
                        {prod.uom}
                      </div>
                    </div>
                    <ArrowRightIcon size={10} className="text-red-800" />
                  </div>
                );
              })}
              {stats.overConsumed > overRows.length && (
                <div className="pl-1 pt-0.5 text-[10px] text-muted-foreground">
                  +{stats.overConsumed - overRows.length} more — filter
                  "Over-consumed" to see all.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
